/* eslint-disable react/prop-types */
"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { BuyerPreferenceEnum, SellerTypeEnum } from "../../../../types";
import { useCookieListener } from "@/components/package-components/hooks/useCookieListener";
import { BuyerPreferenceProps } from "./props";
import TypedText from "@/components/package-components/non-editable/core/typed-text";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const cardVariants = (idx: number) => {
  const y = idx === 0 ? 10 : 30;
  const delay = idx === 0 ? 0.15 : idx * 0.15;

  return {
    hidden: { opacity: 0, y },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "tween",
        duration: 0.4,
        delay,
      },
    },
  };
};

const options: { label: string; value: BuyerPreferenceEnum; src: string }[] = [
  {
    label: "0KM",
    value: BuyerPreferenceEnum.ONLY_ZERO_KM,
    src: "/perfil-click/jeep/compass-azul.webp",
  },
  {
    label: "Seminovos",
    value: BuyerPreferenceEnum.ONLY_USED,
    src: "/perfil-click/jeep/compass-cinza.webp",
  },
];

export const BuyerPreference: React.FC<BuyerPreferenceProps> = ({
  children,
  props,
  setPreference,
  prefVar,
}) => {
  const sellerName = props.data.seller?.sellerName;
  const sellerImage = props.data.seller?.sellerImage;
  const sellerType = props.data?.site?.sellerType ?? SellerTypeEnum.ONLY_USED;

  const [showOptions, setShowOptions] = useState(false);
  const [isClient, setIsClient] = useState<boolean>(false);
  const { cookieValue: currentPreference, setCookieValue } = useCookieListener(
    "BuyerPreference",
    { listen: true, intervalMs: 500 }
  );

  useEffect(() => {
    setIsClient(true);
  }, []);

  const shouldShowSwitchBar =
    sellerType === SellerTypeEnum.HYBRID && currentPreference !== null;

  const handlePreference = (preference: BuyerPreferenceEnum) => {
    setCookieValue(preference);
    setPreference(preference, prefVar);
  };

  const handleInventoryChange = (newPref: BuyerPreferenceEnum) => {
    if (currentPreference !== newPref) {
      handlePreference(newPref);
    }
  };

  if (!isClient) return null;

  if (!currentPreference && sellerType === SellerTypeEnum.HYBRID) {
    return (
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/70 backdrop-blur-sm md:backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="absolute inset-0" />
        <motion.div
          className="w-full max-w-sm mx-auto relative z-10"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: 0.15, delayChildren: 0.3 },
            },
          }}
        >
          <div className="overflow-hidden bg-white rounded shadow-2xl transition-shadow duration-300">
            <div className="flex flex-col items-center pt-8 pb-6 px-4">
              <motion.div
                className="relative mb-4 flex justify-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <div className="relative w-[140px] h-[140px] rounded-full bg-gray-200 flex items-center justify-center z-10 overflow-hidden border border-[#333]">
                  <Image
                    src={sellerImage || ""}
                    alt={sellerName || ""}
                    fill
                    sizes="140px"
                    className="object-cover object-center"
                  />
                </div>
              </motion.div>

              <motion.h2
                className="font-serif mb-1 tracking-[-1px] font-bold text-[32px] text-[#222222] mt-[-18px]"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                {sellerName}
              </motion.h2>

              <TypedText
                text={`Olá, eu sou ${sellerName} e vou te ajudar na compra do seu próximo veículo. Vamos começar?`}
                durationPerCharacter={0.04}
                onComplete={() => setShowOptions(true)}
              />
            </div>

            {showOptions && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
              >
                <div className="flex justify-center pb-2">
                  <h1 className="text-md md:text-lg text-black font-bold text-center">
                    Você está em busca de
                    <span className="text-primary-800">
                      <br />
                      0KM ou Seminovos?
                    </span>
                  </h1>
                </div>

                <motion.div
                  className="flex flex-wrap sm:flex-nowrap gap-4 w-[90%] mx-auto pb-5"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {options.map((opt, idx) => (
                    <motion.button
                      key={opt.value + idx}
                      variants={cardVariants(idx)}
                      whileHover={{
                        scale: 1.02,
                        boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
                      }}
                      whileTap={{
                        scale: 0.98,
                        boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
                      }}
                      onClick={() => handlePreference(opt.value)}
                      className="w-full bg-white rounded border-[1px] border-gray-300 overflow-hidden pb-2"
                      transition={{ type: "tween", duration: idx * 0.4 }}
                    >
                      <div className="relative w-full h-[180px] md:h-[140px]">
                        <div className="absolute inset-0 overflow-hidden -right-24">
                          <Image
                            src={opt.src}
                            alt={`Carro ${opt.label}`}
                            fill
                            className="object-cover object-right"
                            sizes="(min-width: 640px) 50vw, 100vw"
                          />
                        </div>
                      </div>
                      <p className="mt-2 text-sm font-bold text-start ml-2">
                        Comprar{" "}
                        <span className="text-primary-800 uppercase">
                          {opt.label}
                        </span>
                      </p>
                    </motion.button>
                  ))}
                </motion.div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <>
      {shouldShowSwitchBar && (
        <div className="fixed bottom-0 md:bottom-auto md:top-0 z-50 w-full bg-white shadow-sm border-b border-gray-100 transition-all duration-300">
          <div className="max-w-[1024px] mx-auto py-2 px-11 md:px-0">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 py-4 md:py-1">
              <p className="text-sm text-gray-500 hidden md:block">
                Você está visualizando o estoque{" "}
                <span className="font-medium text-primary-800">
                  {currentPreference === BuyerPreferenceEnum.ONLY_USED
                    ? "Seminovos"
                    : "0 KM"}
                </span>
              </p>

              <div className="w-full md:w-auto">
                <div className="flex w-full md:w-[240px] rounded overflow-hidden border border-gray-200">
                  <button
                    onClick={() =>
                      handleInventoryChange(BuyerPreferenceEnum.ONLY_USED)
                    }
                    className={`w-1/2 px-4 py-2 text-sm font-bold transition-colors ${
                      currentPreference === BuyerPreferenceEnum.ONLY_USED
                        ? "bg-primary-800 text-white"
                        : "bg-white text-primary-800 hover:bg-primary-800 hover:text-white"
                    }`}
                  >
                    Seminovos
                  </button>
                  <button
                    onClick={() =>
                      handleInventoryChange(BuyerPreferenceEnum.ONLY_ZERO_KM)
                    }
                    className={`w-1/2 px-4 py-2 text-sm font-bold transition-colors ${
                      currentPreference === BuyerPreferenceEnum.ONLY_ZERO_KM
                        ? "bg-primary-800 text-white"
                        : "bg-white text-primary-800 hover:bg-primary-800 hover:text-white"
                    }`}
                  >
                    0KM
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="pt-2">{children}</div>
    </>
  );
};
